import React, { useCallback, useRef, useState } from 'react';

/**
 * Branded replacement for window.confirm (ROO-220): the test engines used
 * native confirms, which look off-brand next to the cream/green UI and block
 * browser automation. Promise-based so call sites read like confirm():
 *
 *   const { confirm, dialog } = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Exit?', body: '...' }))) return;
 *   ...
 *   return <>{...}{dialog}</>;
 */

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (coral) instead of anchor green. */
  danger?: boolean;
}

export function useConfirm() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    setOpts(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    setOpts(null);
    resolver.current?.(ok);
    resolver.current = null;
  }, []);

  const dialog = opts ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={opts.title}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(26,42,31,.45)', padding: 20,
      }}
      onClick={() => settle(false)}
    >
      <div
        style={{
          background: '#FFFBF0', borderRadius: 20, border: '1.5px solid #EFE6D0',
          maxWidth: 400, width: '100%', padding: '26px 26px 22px',
          fontFamily: "'Lexend', system-ui, sans-serif",
          boxShadow: '0 18px 48px -18px rgba(26,42,31,.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: 17, color: '#1A2A1F' }}>{opts.title}</h3>
        {opts.body && (
          <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.6, color: '#6B7A6F' }}>{opts.body}</p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 }}>
          <button
            type="button"
            autoFocus
            onClick={() => settle(false)}
            style={{
              border: '1.5px solid #E5D9BD', background: '#fff', color: '#1A2A1F',
              borderRadius: 999, padding: '9px 18px', fontWeight: 700, fontSize: 13.5,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {opts.cancelLabel || 'Keep going'}
          </button>
          <button
            type="button"
            onClick={() => settle(true)}
            style={{
              border: 0, background: opts.danger ? '#E26B4F' : '#14593C', color: '#fff',
              borderRadius: 999, padding: '9px 18px', fontWeight: 700, fontSize: 13.5,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {opts.confirmLabel || 'Yes'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, dialog };
}
